import { EsploraAddressData } from '@ducat-unit/client-sdk';
import { DefaultError, QueryClient, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { QueryKeys } from './query-keys';
import { get } from './api.helpers';
import { WalletType } from '@/context/types';
import { fetchSatsUtxosUnisat } from '@/lib/connector-unisat';

type WalletBalanceParams = {
  esploraUrl: string;
  ordUrl: string;
  address: string | null,
  walletType: WalletType | null;
};

type WalletBalanceResult = { sats_bal: number };

type QueryOptionsParam = Omit<
  UseQueryOptions<WalletBalanceResult, DefaultError>,
  'queryKey' | 'queryFn'
>;

async function fetchSatsBalance(esploraUrl: string, address: string | null) {
  const url = `${esploraUrl}/address/${address}`;
  const result: EsploraAddressData = await get(url);
  const balance = result.chain_stats.funded_txo_sum - result.chain_stats.spent_txo_sum;

  return { sats_bal: balance };
}

async function fetchSatsBalanceUnisat(ordUrl: string, address: string | null) {
  const utxos = address ? await fetchSatsUtxosUnisat(ordUrl, address) : [];
  const balance = utxos.reduce((acc, utxo) => acc + utxo.value, 0);

  return { sats_bal: balance };
}

export function useQueryWalletBalance(params: WalletBalanceParams, options?: QueryOptionsParam) {
  const { esploraUrl, ordUrl, address, walletType } = params;
  const queryKey = [QueryKeys.walletBalance, address]
  return useQuery({
    queryKey,
    queryFn: async () => {
      if (walletType === WalletType.Unisat) {
        return fetchSatsBalanceUnisat(ordUrl, address);
      } else {
        return fetchSatsBalance(esploraUrl, address);
      }
    },
    staleTime: 60 * 1000,
    enabled: !!address,
    ...options
  });
}

export function invalidateWalletBalance(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [QueryKeys.walletBalance] });
}
